// Shared interface pieces: escaping, loading/empty/error states, toasts and keyboard helpers.
import { ApiError } from "./api.ts";
import { icon } from "./icons.ts";

const ENTITIES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
export const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, (c) => ENTITIES[c]!);
export const $ = <E extends Element = HTMLElement>(root: ParentNode, sel: string) => root.querySelector<E>(sel)!;
export const $$ = <E extends Element = HTMLElement>(root: ParentNode, sel: string) => [...root.querySelectorAll<E>(sel)];

export const skeleton = (rows = 4, tall = false) =>
  `<div class="skeleton" aria-busy="true" aria-label="Cargando">${Array.from({ length: rows }, (_, i) =>
    `<i class="${tall ? "tall" : ""}" style="width:${tall ? 100 : 92 - (i % 3) * 18}%"></i>`).join("")}</div>`;

export const empty = (ico: string, title: string, text: string, action = "") =>
  `<div class="empty">${icon(ico)}<h2>${esc(title)}</h2><p>${esc(text)}</p>${action}</div>`;

export function failure(err: unknown) {
  const p = err instanceof ApiError ? err.problem : null;
  return `<div class="failure" role="alert">${icon("circle-alert")}<h2>${esc(p?.title ?? "Algo ha fallado")}</h2>
    <p>${esc(p?.detail ?? "Vuelve a intentarlo en un momento.")}</p>
    <button class="btn" type="button" data-retry>${icon("refresh-cw", "sm")} Reintentar</button></div>`;
}

/** Loading → content | error with retry. */
export async function load<T>(box: HTMLElement, opts: { fetch: () => Promise<T>; render: (data: T) => void; placeholder?: string }) {
  box.innerHTML = opts.placeholder ?? skeleton();
  try {
    opts.render(await opts.fetch());
  } catch (err) {
    box.innerHTML = failure(err);
    $(box, "[data-retry]").addEventListener("click", () => void load(box, opts));
    if (!(err instanceof ApiError)) console.error(err);
  }
}

export function toast(text: string, kind: "" | "error" = "") {
  const el = document.createElement("div");
  el.className = `toast ${kind}`;
  el.innerHTML = `${icon(kind === "error" ? "circle-alert" : "check", "sm")}<span>${esc(text)}</span>`;
  document.getElementById("toasts")!.append(el);
  setTimeout(() => el.remove(), 4200);
}

export const errorText = (err: unknown) => (err instanceof ApiError ? err.problem.detail ?? err.problem.title : "Algo ha fallado");

/** Arrow keys, Home and End across a tablist or radiogroup. */
export function arrowKeys(list: HTMLElement, onSelect: (el: HTMLElement) => void) {
  list.addEventListener("keydown", (e) => {
    const items = $$(list, '[role="tab"], [role="radio"]');
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    const to = ({ ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: items.length - 1 } as Record<string, number>)[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const target = items[(to + items.length) % items.length]!;
    target.focus();
    onSelect(target);
  });
}

export const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

export function fieldError(form: HTMLElement, name: string, message?: string) {
  form.querySelector(`[name="${name}"]`)?.setAttribute("aria-invalid", message ? "true" : "false");
  const box = form.querySelector(`[data-error="${name}"]`);
  if (box) box.innerHTML = message ? `${icon("circle-alert", "sm")}<span>${esc(message)}</span>` : "";
}

export const busy = (b: HTMLButtonElement, text: string) => { const prev = b.innerHTML; b.disabled = true; b.innerHTML = `${icon("refresh-cw", "sm spin")} ${esc(text)}`; return () => { b.disabled = false; b.innerHTML = prev; }; };
